// Excel Importer - Parse uploaded Excel file and extract resource data

import ExcelJS from 'exceljs';
import { SHEET_NAMES, COLUMN_MAPPINGS, DATA_START_ROW, HIERARCHY_STRUCTURE_ROW } from '../excel/constants.js';

/**
 * ExcelImporter class
 * Parses uploaded Excel files and extracts resource data for import
 */
class ExcelImporter {
  constructor() {
    this.workbook = null;
    this.parsedData = {};
  }

  /**
   * Load Excel file from File object (browser upload)
   */
  async loadFile(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      this.workbook = new ExcelJS.Workbook();
      await this.workbook.xlsx.load(arrayBuffer);
      return true;
    } catch (error) {
      console.error('Error loading Excel file:', error);
      throw new Error(`Failed to load Excel file: ${error.message}`);
    }
  }

  /**
   * Check if workbook is loaded
   */
  isLoaded() {
    return this.workbook !== null;
  }

  /**
   * Get worksheet by name
   */
  getWorksheet(sheetName) {
    if (!this.workbook) {
      throw new Error('Workbook not loaded. Call loadFile() first.');
    }

    const worksheet = this.workbook.getWorksheet(sheetName);
    if (!worksheet) {
      console.warn(`Worksheet "${sheetName}" not found in workbook`);
      return null;
    }

    return worksheet;
  }

  /**
   * Parse Hours of Operation sheet
   */
  parseHoursOfOperation() {
    const sheetName = SHEET_NAMES.HOURS_OF_OPERATION;
    const columns = COLUMN_MAPPINGS.HOURS_OF_OPERATION;
    const startRow = DATA_START_ROW.HOURS_OF_OPERATION;

    const worksheet = this.getWorksheet(sheetName);
    if (!worksheet) {
      return [];
    }

    const data = [];
    let rowIndex = startRow;

    while (rowIndex <= worksheet.rowCount) {
      const row = worksheet.getRow(rowIndex);

      // Check if row has data (Name column not empty)
      const name = row.getCell(columns.NAME).value;
      if (!name || name === '') {
        rowIndex++;
        continue;
      }

      // Stop at empty row
      if (name === null || name === undefined) {
        break;
      }

      data.push({
        name: this.getCellValue(row, columns.NAME),
        description: this.getCellValue(row, columns.DESCRIPTION),
        hoursOfOperation: this.getCellValue(row, columns.HOURS_OF_OPERATION),
        timeZone: this.getCellValue(row, columns.TIME_ZONE),
        rowNumber: rowIndex
      });

      rowIndex++;
    }

    return data;
  }

  /**
   * Parse Agent Status sheet
   */
  parseAgentStatus() {
    const sheetName = SHEET_NAMES.AGENT_STATUS;
    const columns = COLUMN_MAPPINGS.AGENT_STATUS;
    const startRow = DATA_START_ROW.AGENT_STATUS;

    const worksheet = this.getWorksheet(sheetName);
    if (!worksheet) {
      return [];
    }

    const data = [];
    let rowIndex = startRow;

    while (rowIndex <= worksheet.rowCount) {
      const row = worksheet.getRow(rowIndex);

      const name = row.getCell(columns.NAME).value;
      if (!name || name === '') {
        rowIndex++;
        continue;
      }

      if (name === null || name === undefined) {
        break;
      }

      data.push({
        name: this.getCellValue(row, columns.NAME),
        description: this.getCellValue(row, columns.DESCRIPTION),
        rowNumber: rowIndex
      });

      rowIndex++;
    }

    return data;
  }

  /**
   * Parse Queues sheet
   */
  parseQueues() {
    const sheetName = SHEET_NAMES.QUEUES;
    const columns = COLUMN_MAPPINGS.QUEUES;
    const startRow = DATA_START_ROW.QUEUES;

    const worksheet = this.getWorksheet(sheetName);
    if (!worksheet) {
      return [];
    }

    const data = [];
    let rowIndex = startRow;

    while (rowIndex <= worksheet.rowCount) {
      const row = worksheet.getRow(rowIndex);

      const name = row.getCell(columns.NAME).value;
      if (!name || name === '') {
        rowIndex++;
        continue;
      }

      if (name === null || name === undefined) {
        break;
      }

      data.push({
        name: this.getCellValue(row, columns.NAME),
        description: this.getCellValue(row, columns.DESCRIPTION),
        hoursOfOperationName: this.getCellValue(row, columns.HOURS_OF_OPERATION),
        outboundCallerIdName: this.getCellValue(row, columns.OUTBOUND_CALLER_ID_NAME),
        outboundCallerIdNumber: this.getCellValue(row, columns.OUTBOUND_CALLER_ID_NUMBER),
        outboundFlowName: this.getCellValue(row, columns.OUTBOUND_WHISPER_FLOW_ID),
        maxContacts: this.getCellValue(row, columns.MAX_CONTACTS),
        rowNumber: rowIndex
      });

      rowIndex++;
    }

    return data;
  }

  /**
   * Parse Quick Connects sheet
   */
  parseQuickConnects() {
    const sheetName = SHEET_NAMES.QUICK_CONNECTS;
    const columns = COLUMN_MAPPINGS.QUICK_CONNECTS;
    const startRow = DATA_START_ROW.QUICK_CONNECTS;

    const worksheet = this.getWorksheet(sheetName);
    if (!worksheet) {
      return [];
    }

    const data = [];
    let rowIndex = startRow;

    while (rowIndex <= worksheet.rowCount) {
      const row = worksheet.getRow(rowIndex);

      const name = row.getCell(columns.NAME).value;
      if (!name || name === '') {
        rowIndex++;
        continue;
      }

      if (name === null || name === undefined) {
        break;
      }

      data.push({
        name: this.getCellValue(row, columns.NAME),
        description: this.getCellValue(row, columns.DESCRIPTION),
        type: this.getCellValue(row, columns.TYPE),
        destination: this.getCellValue(row, columns.DESTINATION), // Could be phone, queue name, or user name
        targetName: this.getCellValue(row, columns.DESTINATION), // Alias for consistency
        contactFlowName: this.getCellValue(row, columns.CONTACT_FLOW_NAME),
        rowNumber: rowIndex
      });

      rowIndex++;
    }

    return data;
  }

  /**
   * Parse Routing Profiles sheet
   */
  parseRoutingProfiles() {
    const sheetName = SHEET_NAMES.ROUTING_PROFILES;
    const columns = COLUMN_MAPPINGS.ROUTING_PROFILES;
    const startRow = DATA_START_ROW.ROUTING_PROFILES;

    const worksheet = this.getWorksheet(sheetName);
    if (!worksheet) {
      return [];
    }

    const data = [];
    let rowIndex = startRow;

    while (rowIndex <= worksheet.rowCount) {
      const row = worksheet.getRow(rowIndex);

      const name = row.getCell(columns.NAME).value;
      if (!name || name === '') {
        rowIndex++;
        continue;
      }

      if (name === null || name === undefined) {
        break;
      }

      data.push({
        name: this.getCellValue(row, columns.NAME),
        description: this.getCellValue(row, columns.DESCRIPTION),
        channels: this.getCellValue(row, columns.CHANNELS),
        chatConcurrency: this.getCellValue(row, columns.CHAT_CONCURRENCY),
        taskConcurrency: this.getCellValue(row, columns.TASK_CONCURRENCY),
        emailConcurrency: this.getCellValue(row, columns.EMAIL_CONCURRENCY),
        defaultOutboundQueue: this.getCellValue(row, columns.DEFAULT_OUTBOUND_QUEUE),
        associatedQueues: this.getCellValue(row, columns.ASSOCIATED_QUEUES),
        rowNumber: rowIndex
      });

      rowIndex++;
    }

    return data;
  }

  /**
   * Parse User Hierarchies sheet
   */
  parseUserHierarchies() {
    const sheetName = SHEET_NAMES.USER_HIERARCHIES;
    const columns = COLUMN_MAPPINGS.USER_HIERARCHY_DATA;
    const startRow = DATA_START_ROW.USER_HIERARCHY_DATA;

    const worksheet = this.getWorksheet(sheetName);
    if (!worksheet) {
      return [];
    }

    const data = [];
    let rowIndex = startRow;

    while (rowIndex <= worksheet.rowCount) {
      const row = worksheet.getRow(rowIndex);

      // Check if at least Level 1 has data
      const level1 = row.getCell(columns.LEVEL_ONE).value;
      if (!level1 || level1 === '' || level1 === 'N/A') {
        rowIndex++;
        continue;
      }

      if (level1 === null || level1 === undefined) {
        break;
      }

      data.push({
        level1: this.getCellValue(row, columns.LEVEL_ONE),
        level2: this.getCellValue(row, columns.LEVEL_TWO),
        level3: this.getCellValue(row, columns.LEVEL_THREE),
        level4: this.getCellValue(row, columns.LEVEL_FOUR),
        level5: this.getCellValue(row, columns.LEVEL_FIVE),
        rowNumber: rowIndex
      });

      rowIndex++;
    }

    return data;
  }

  /**
   * Parse Hierarchy Structure (Row 5 - level names)
   * This defines the names for each level in the hierarchy (e.g., "Region", "Site", "Team")
   */
  parseHierarchyStructure() {
    const worksheet = this.getWorksheet(SHEET_NAMES.USER_HIERARCHIES);
    if (!worksheet) {
      return null;
    }

    const structureRow = worksheet.getRow(HIERARCHY_STRUCTURE_ROW);
    const columns = COLUMN_MAPPINGS.USER_HIERARCHY_DATA;

    const levelOne = this.getCellValue(structureRow, columns.LEVEL_ONE);
    const levelTwo = this.getCellValue(structureRow, columns.LEVEL_TWO);
    const levelThree = this.getCellValue(structureRow, columns.LEVEL_THREE);
    const levelFour = this.getCellValue(structureRow, columns.LEVEL_FOUR);
    const levelFive = this.getCellValue(structureRow, columns.LEVEL_FIVE);

    // Only return structure if at least level one is defined
    if (!levelOne || levelOne === 'N/A') {
      return null;
    }

    return {
      levelOne,
      levelTwo: levelTwo || null,
      levelThree: levelThree || null,
      levelFour: levelFour || null,
      levelFive: levelFive || null
    };
  }

  /**
   * Parse all resource types
   */
  parseAll() {
    if (!this.isLoaded()) {
      throw new Error('Workbook not loaded. Call loadFile() first.');
    }

    this.parsedData = {
      hoursOfOperation: this.parseHoursOfOperation(),
      agentStatus: this.parseAgentStatus(),
      queues: this.parseQueues(),
      quickConnects: this.parseQuickConnects(),
      routingProfiles: this.parseRoutingProfiles(),
      userHierarchies: this.parseUserHierarchies(),
      hierarchyStructure: this.parseHierarchyStructure()
    };

    return this.parsedData;
  }

  /**
   * Get parsed data
   */
  getParsedData() {
    return this.parsedData;
  }

  /**
   * Helper method to safely get cell value
   */
  getCellValue(row, columnLetter) {
    try {
      const cell = row.getCell(columnLetter);
      const value = cell.value;

      // Handle null, undefined, or empty string
      if (value === null || value === undefined || value === '') {
        return '';
      }

      // Handle rich text (ExcelJS rich text format)
      if (value && typeof value === 'object' && value.richText) {
        return value.richText.map(rt => rt.text).join('');
      }

      // Handle formula results
      if (value && typeof value === 'object' && value.result !== undefined) {
        return value.result;
      }

      // Convert to string and trim
      return String(value).trim();
    } catch (error) {
      console.warn(`Error reading cell ${columnLetter}:`, error);
      return '';
    }
  }

  /**
   * Get summary of parsed data
   */
  getSummary() {
    const summary = {};

    Object.keys(this.parsedData).forEach(resourceType => {
      const resources = this.parsedData[resourceType];
      summary[resourceType] = {
        count: Array.isArray(resources) ? resources.length : 0
      };
    });

    return summary;
  }

  /**
   * Validate that required sheets exist
   */
  validateSheets() {
    if (!this.isLoaded()) {
      return {
        valid: false,
        missingSheets: ['Workbook not loaded']
      };
    }

    const requiredSheets = [
      SHEET_NAMES.HOURS_OF_OPERATION,
      SHEET_NAMES.AGENT_STATUS,
      SHEET_NAMES.QUEUES,
      SHEET_NAMES.QUICK_CONNECTS,
      SHEET_NAMES.ROUTING_PROFILES,
      SHEET_NAMES.USER_HIERARCHIES
    ];

    const missingSheets = [];

    requiredSheets.forEach(sheetName => {
      const worksheet = this.workbook.getWorksheet(sheetName);
      if (!worksheet) {
        missingSheets.push(sheetName);
      }
    });

    return {
      valid: missingSheets.length === 0,
      missingSheets: missingSheets
    };
  }
}

export default ExcelImporter;
